// src/pages/ImportData.js
import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { colors } from '../theme/tokens';

const ImportData = () => {
  const { token, canApprove } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  React.useEffect(() => {
    if (!canApprove) {
      navigate('/');
    }
  }, [canApprove, navigate]);

  const downloadTemplate = () => {
    // Create CSV template with headers and example rows
    const templateData = [
      ['Make', 'Model', 'Variant', 'yearRange', 'position', 'part code', 'qty', 'UOM'],
      ['Toyota', 'Corolla', '1.6 GLi', '2010-2015', 'Front Door Seal', 'PR310', '3', 'm'],
      ['BMW', '3 Series', '320i', '2015-2020', 'Rear Bumper Seal', 'PR450', '2', 'm'],
      ['Audi', 'A4', '2.0 TFSI', '2018-2022', 'Boot Seal', 'PR500', '1', 'x'],
      ['Ford', 'Focus', 'ST', '2012-2018', 'Window Seal', 'PR250', '4', 'm']
    ];

    // Convert to CSV string
    const csvContent = templateData.map(row => row.join(',')).join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'car_parts_import_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showToast('Please upload a CSV file', 'warning');
      return;
    }

    setLoading(true);
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = parseCSV(text);
        
        if (rows.length === 0) {
          showToast('CSV file is empty', 'warning');
          setLoading(false);
          return;
        }

        const response = await axios.post(
          `${API_URL}/api/import/parse-csv`,
          { data: rows },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setCsvData(response.data.data);
        showToast(`Loaded ${response.data.total} rows from CSV`, 'success');
      } catch (error) {
        console.error('CSV parse error:', error);
        showToast('Failed to parse CSV: ' + (error.response?.data?.error || error.message), 'error');
      } finally {
        setLoading(false);
      }
    };

    reader.onerror = () => {
      showToast('Failed to read file', 'error');
      setLoading(false);
    };

    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }

    return rows;
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({ ...row });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = () => {
    setCsvData(csvData.map(row => row.id === editingId ? { ...editForm } : row));
    setEditingId(null);
    setEditForm({});
  };

  const handleEditChange = (field, value) => {
    setEditForm({ ...editForm, [field]: value });
  };

  const deleteRow = async (id) => {
    const ok = await confirm('Remove this row from the import list?', {
      title: 'Remove Row',
      icon: '🗑️',
      confirmText: 'Remove',
      danger: true
    });
    if (ok) {
      setCsvData(csvData.filter(row => row.id !== id));
    }
  };

  const importSingle = async (row) => {
    if (row.errors) {
      showToast('Cannot import row with errors: ' + row.errors.join(', '), 'error');
      return;
    }

    try {
      setImporting(true);
      const response = await axios.post(
        `${API_URL}/api/import/import-single`,
        row,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCsvData(prev => prev.map(r => 
        r.id === row.id ? { ...r, status: 'imported', message: response.data.message } : r
      ));
      showToast(response.data.message, 'success');
    } catch (error) {
      showToast('Import failed: ' + (error.response?.data?.error || error.message), 'error');
      setCsvData(prev => prev.map(r => 
        r.id === row.id ? { ...r, status: 'error', message: error.response?.data?.error } : r
      ));
    } finally {
      setImporting(false);
    }
  };

  const importAll = async () => {
    const rowsToImport = csvData.filter(row => !row.errors && row.status !== 'imported');
    
    if (rowsToImport.length === 0) {
      showToast('No rows to import', 'warning');
      return;
    }

    const ok = await confirm(`Import ${rowsToImport.length} rows into the database?`, {
      title: 'Bulk Import',
      icon: '📤',
      confirmText: 'Import All'
    });
    if (!ok) return;

    try {
      setImporting(true);
      const response = await axios.post(
        `${API_URL}/api/import/import-bulk`,
        { data: rowsToImport },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { results } = response.data;
      
      setCsvData(prev => prev.map(row => {
        if (!row.errors && row.status !== 'imported') {
          return { ...row, status: 'imported', message: 'Imported successfully' };
        }
        return row;
      }));

      showToast(
        `Bulk import complete! ✅ ${results.success} success, 📝 ${results.created} created, 🔄 ${results.updated} updated, ❌ ${results.failed} failed`,
        results.failed > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      showToast('Bulk import failed: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setImporting(false);
    }
  };

  const clearData = async () => {
    const ok = await confirm('Clear all data from the import table?', {
      title: 'Clear Table',
      icon: '🗑️',
      confirmText: 'Clear All',
      danger: true
    });
    if (ok) {
      setCsvData([]);
      showToast('Table cleared', 'info');
    }
  };

  const stats = {
    total: csvData.length,
    errors: csvData.filter(r => r.errors).length,
    imported: csvData.filter(r => r.status === 'imported').length,
    pending: csvData.filter(r => !r.errors && r.status !== 'imported').length
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 CSV Import Tool</h1>
        <p style={styles.subtitle}>Import car parts data in bulk from CSV files</p>
      </div>

      {/* Upload Section */}
      <div style={styles.uploadCard}>
        <div style={styles.uploadContent}>
          <h3>Upload CSV File</h3>
          <p style={styles.formatInfo}>
            Required columns: <strong>Make, Model, Variant, yearRange, position, part code, qty, UOM</strong>
          </p>
          <p style={styles.exampleText}>
            Example yearRange: <code>2018-2022</code> or <code>2018 - 2022</code>
          </p>
          
          {/* Download Template Button */}
          <div style={styles.templateSection}>
            <button
              onClick={downloadTemplate}
              style={styles.downloadTemplateBtn}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              type="button"
            >
              📥 Download CSV Template
            </button>
            <small style={styles.templateHint}>
              Download an example template with sample data to get started
            </small>
          </div>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={styles.fileInput}
            disabled={loading}
          />
          {loading && <div style={styles.loadingText}>Parsing CSV...</div>}
        </div>
      </div>

      {/* Statistics */}
      {csvData.length > 0 && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.total}</div>
            <div style={styles.statLabel}>Total Rows</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statNumber, color: colors.parGreen}}>{stats.pending}</div>
            <div style={styles.statLabel}>Ready to Import</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statNumber, color: colors.blue}}>{stats.imported}</div>
            <div style={styles.statLabel}>Imported</div>
          </div>
          <div style={styles.statCard}>
            <div style={{...styles.statNumber, color: colors.red}}>{stats.errors}</div>
            <div style={styles.statLabel}>Errors</div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {csvData.length > 0 && (
        <div style={styles.actions}>
          <button
            onClick={importAll}
            disabled={importing || stats.pending === 0}
            style={styles.importAllBtn}
          >
            {importing ? 'Importing...' : `📤 Import All (${stats.pending})`}
          </button>
          <button onClick={clearData} style={styles.clearBtn}>
            🗑 Clear Table
          </button>
        </div>
      )}

      {/* Data Table */}
      {csvData.length > 0 && (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Make</th>
                <th style={styles.th}>Model</th>
                <th style={styles.th}>Variant</th>
                <th style={styles.th}>Year Range</th>
                <th style={styles.th}>Position</th>
                <th style={styles.th}>Part Code</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>UOM</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {csvData.map(row => (
                <tr 
                  key={row.id} 
                  style={{
                    ...styles.tr,
                    background: row.errors ? colors.redSoft : row.status === 'imported' ? colors.greenSoft : 'transparent'
                  }}
                >
                  {editingId === row.id ? (
                    // Edit Mode
                    <>
                      <td style={styles.td}>
                        <span style={styles.statusBadge}>Editing</span>
                      </td>
                      <td style={styles.td}>
                        <input value={editForm.make} onChange={(e) => handleEditChange('make', e.target.value)} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <input value={editForm.model} onChange={(e) => handleEditChange('model', e.target.value)} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <input value={editForm.variant} onChange={(e) => handleEditChange('variant', e.target.value)} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <div style={styles.yearInputs}>
                          <input 
                            type="number" 
                            value={editForm.yearFrom} 
                            onChange={(e) => handleEditChange('yearFrom', parseInt(e.target.value))} 
                            style={styles.yearInput} 
                            placeholder="From"
                          />
                          <span>-</span>
                          <input 
                            type="number" 
                            value={editForm.yearTo} 
                            onChange={(e) => handleEditChange('yearTo', parseInt(e.target.value))} 
                            style={styles.yearInput}
                            placeholder="To"
                          />
                        </div>
                      </td>
                      <td style={styles.td}>
                        <input value={editForm.position} onChange={(e) => handleEditChange('position', e.target.value)} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <input value={editForm.partCode} onChange={(e) => handleEditChange('partCode', e.target.value)} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" value={editForm.quantity} onChange={(e) => handleEditChange('quantity', parseFloat(e.target.value))} style={styles.editInput} />
                      </td>
                      <td style={styles.td}>
                        <select value={editForm.unit} onChange={(e) => handleEditChange('unit', e.target.value)} style={styles.editInput}>
                          <option value="x">x</option>
                          <option value="m">m</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <button onClick={saveEdit} style={{...styles.actionBtn, ...styles.saveBtn}}>✓</button>
                        <button onClick={cancelEdit} style={{...styles.actionBtn, ...styles.cancelBtn}}>✗</button>
                      </td>
                    </>
                  ) : (
                    // View Mode
                    <>
                      <td style={styles.td}>
                        {row.errors ? (
                          <span style={{...styles.statusBadge, ...styles.errorBadge}} title={row.errors.join(', ')}>
                            ❌ Error
                          </span>
                        ) : row.status === 'imported' ? (
                          <span style={{...styles.statusBadge, ...styles.successBadge}}>
                            ✅ Imported
                          </span>
                        ) : (
                          <span style={{...styles.statusBadge, ...styles.pendingBadge}}>
                            ⏳ Pending
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{row.make}</td>
                      <td style={styles.td}>{row.model}</td>
                      <td style={styles.td}>{row.variant}</td>
                      <td style={styles.td}>{row.yearFrom} - {row.yearTo}</td>
                      <td style={styles.td}>{row.position}</td>
                      <td style={styles.td}><code>{row.partCode}</code></td>
                      <td style={styles.td}>{row.quantity}</td>
                      <td style={styles.td}>{row.unit}</td>
                      <td style={styles.td}>
                        {row.status !== 'imported' && !row.errors && (
                          <>
                            <button 
                              onClick={() => importSingle(row)} 
                              disabled={importing}
                              style={{...styles.actionBtn, ...styles.importBtn}}
                              title="Import this row"
                            >
                              📤
                            </button>
                            <button 
                              onClick={() => startEdit(row)} 
                              style={{...styles.actionBtn, ...styles.editBtn}}
                              title="Edit this row"
                            >
                              ✎
                            </button>
                          </>
                        )}
                        <button 
                          onClick={() => deleteRow(row.id)} 
                          style={{...styles.actionBtn, ...styles.deleteBtn}}
                          title="Remove from list"
                        >
                          🗑
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {csvData.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <p>📁 No CSV file loaded</p>
          <p style={styles.emptyHint}>Upload a CSV file to begin importing data</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1400px', margin: '20px auto', padding: '20px' },
  header: { textAlign: 'center', marginBottom: '30px' },
  title: { color: colors.text, marginBottom: '10px' },
  subtitle: { color: colors.textMuted, fontSize: '14px' },
  uploadCard: { background: colors.surface, color: colors.text, padding: '30px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '20px', border: `1px solid ${colors.border}` },
  uploadContent: { textAlign: 'center' },
  formatInfo: { color: colors.textMuted, marginBottom: '10px' },
  exampleText: { color: colors.textFaint, fontSize: '12px', marginBottom: '20px' },
  templateSection: { background: colors.greenSoft, padding: '20px', borderRadius: '10px', marginBottom: '20px', border: '2px dashed #2fb65c' },
  downloadTemplateBtn: { padding: '12px 24px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginBottom: '10px', transition: 'all 0.3s' },
  templateHint: { display: 'block', color: colors.textMuted, fontSize: '12px' },
  fileInput: { padding: '10px', fontSize: '14px', cursor: 'pointer', color: colors.text },
  loadingText: { marginTop: '10px', color: colors.parGreenBright, fontWeight: '600' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' },
  statCard: { background: colors.surface, padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', textAlign: 'center', border: `1px solid ${colors.border}` },
  statNumber: { fontSize: '32px', fontWeight: 'bold', color: colors.text },
  statLabel: { fontSize: '12px', color: colors.textMuted, marginTop: '5px' },
  actions: { display: 'flex', gap: '10px', marginBottom: '20px' },
  importAllBtn: { flex: 1, padding: '15px', background: colors.parGreen, color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  clearBtn: { padding: '15px 30px', background: colors.red, color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  tableContainer: { background: colors.surface, color: colors.text, borderRadius: '10px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', maxHeight: '600px', border: `1px solid ${colors.border}` },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '12px', borderBottom: `2px solid ${colors.border}`, fontWeight: '600', color: colors.text, background: colors.surfaceSoft, position: 'sticky', top: 0, zIndex: 1 },
  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: '10px', verticalAlign: 'middle' },
  statusBadge: { padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', display: 'inline-block' },
  errorBadge: { background: colors.redSoft, color: colors.red },
  successBadge: { background: colors.greenSoft, color: colors.parGreen },
  pendingBadge: { background: colors.amberSoft, color: colors.amber },
  editInput: { width: '100%', padding: '6px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '12px', background: colors.inputBg, color: colors.text },
  yearInputs: { display: 'flex', gap: '5px', alignItems: 'center' },
  yearInput: { width: '60px', padding: '6px', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '12px', background: colors.inputBg, color: colors.text },
  actionBtn: { padding: '6px 10px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', marginRight: '5px' },
  importBtn: { background: colors.blue, color: 'white' },
  editBtn: { background: colors.amber, color: 'white' },
  deleteBtn: { background: colors.red, color: 'white' },
  saveBtn: { background: colors.parGreen, color: 'white' },
  cancelBtn: { background: colors.surface2, color: colors.text },
  emptyState: { textAlign: 'center', padding: '60px', background: colors.surface, color: colors.text, borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  emptyHint: { color: colors.textFaint, fontSize: '14px', marginTop: '10px' }
};

export default ImportData;
